import React, { memo, useEffect, useRef } from "react"
import { Draggable, Droppable } from "react-beautiful-dnd"
import { Button, ButtonGroup, Col, Form, Row } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"

import Icon from "../../../../../common/icon/icon.component"
import ImageUploaderDropZone from "../../../../../common/image-uploader/dropzone.component.jsx"
import { CustomAttributesMultipleSelectWithImageSupportProps } from "../../../../../common/types"
import IntlProps from "../../../../../state/helpers/IntlProps"
import {
    CUSTOM_ATTRIBUTES_MESSAGES,
    CUSTOM_ATTRIBUTE_EVENT, handleCustomAttributeMutliImageOperation,
    isCustomDefinitionAttributePropsEqual
} from "../../../../../utils"

const CustomAttributesMultipleSelectWithImageSupport =
    (props: CustomAttributesMultipleSelectWithImageSupportProps & IntlProps) => {
        const metadata = props.customAttributes[props.attributeIndex].customFieldDefinitionLabel?.metadata ?? []
        /*
        * Value persistent across all component life cycyle
        * Make sure in each child handleCustomAttributeMutliImageOperation has the latest props passed as arguement
        */
        const attributes = useRef(props.customAttributes)
        useEffect(() => {
            attributes.current = [...props.customAttributes]
        }, [
            props.customAttributes
        ])

        return <>
            <Droppable
                droppableId={`${props.attributeIndex}`}
                type={"droppableSubItem"}
            >
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        className={"group-sortable " + (snapshot.isDraggingOver ? "dnd-highlight" : "")}
                    >
                        <Row className="mb-3">
                            {props.intl.formatMessage(
                                CUSTOM_ATTRIBUTES_MESSAGES["allow_more_than_more_choice_possible_values_label"]
                            )}
                        </Row>
                        {([...metadata] || []).map((input, index) => (
                            <Draggable
                                key={input.id + index}
                                draggableId={index + input.label + "draggable2"}
                                index={index}
                            >
                                {(provided) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                    >
                                        <Row className="mb-3">
                                            <Col xs={1}>
                                                <Row className="d-flex justify-content-center">
                                                    <a
                                                        href={"#"}
                                                        onClick={e => e.preventDefault()}
                                                        {...provided.dragHandleProps}
                                                    >
                                                        <Icon className={"lead"} value={"bars"} />
                                                    </a>
                                                </Row>
                                            </Col>
                                            <Col xs={5}>
                                                <Form.Control
                                                    type="text"
                                                    placeholder={
                                                        props.intl.formatMessage(
                                                            CUSTOM_ATTRIBUTES_MESSAGES["custom_attribute_placeholder"]
                                                        )
                                                    }
                                                    onChange={event => handleCustomAttributeMutliImageOperation(
                                                        attributes.current,
                                                        props.attributeIndex,
                                                        index,
                                                        event.target.value,
                                                        CUSTOM_ATTRIBUTE_EVENT.UPDATE_ATTRIBUTE_LABEL,
                                                        props.onCustomAttributeDefnChange
                                                    )
                                                    }
                                                    value={input.label || ""}
                                                    autoFocus={true}
                                                />
                                            </Col>
                                            <Col xs={4}>
                                                <ImageUploaderDropZone
                                                    onUpdateImageList={images =>
                                                        handleCustomAttributeMutliImageOperation(
                                                            attributes.current,
                                                            props.attributeIndex,
                                                            index,
                                                            images[0],
                                                            CUSTOM_ATTRIBUTE_EVENT.UPDATE_MULTI_IMAGE_SELECT_ID,
                                                            props.onCustomAttributeDefnChange
                                                        )
                                                    }
                                                    maxFiles={1}
                                                    images={(input.images || []).length > 0 ? input.images : []}
                                                />
                                            </Col>
                                            <Col xs={2}>
                                                <Button
                                                    variant="danger"
                                                    className="mb-2"
                                                    onClick={() => handleCustomAttributeMutliImageOperation(
                                                        attributes.current,
                                                        props.attributeIndex,
                                                        index,
                                                        undefined,
                                                        CUSTOM_ATTRIBUTE_EVENT.DELETE_ATTRIBUTE,
                                                        props.onCustomAttributeDefnChange
                                                    )}>
                                                    <FormattedMessage
                                                        id="product.custom_attribute.remove_attr_btn"
                                                        defaultMessage="Remove"
                                                    />
                                                </Button>
                                            </Col>
                                        </Row>
                                        <hr />
                                    </div>
                                )}
                            </Draggable>
                        ))}

                    </div>
                )}
            </Droppable>
            <Row className="mb-3">
                <Col xs="auto">
                    <ButtonGroup aria-label="Add an attribute" >
                        <Button onClick={() => handleCustomAttributeMutliImageOperation(
                            attributes.current,
                            props.attributeIndex,
                            undefined,
                            undefined,
                            CUSTOM_ATTRIBUTE_EVENT.ADD_ATTRIBUTE,
                            props.onCustomAttributeDefnChange
                        )} variant="primary">
                            <FormattedMessage
                                id="product.custom_attribute.add_multi_image_attr_btn"
                                defaultMessage="Add a value"
                            />
                        </Button>
                    </ButtonGroup>
                </Col>
            </Row>
        </>
    }

function areEqual(
    { attributeIndex: prevPropsAttributeIndex, customAttributes: prevPropsCustomAttributes },
    { attributeIndex: nextPropsAttributeIndex, customAttributes: nextPropsCustomAttributes }
) {
    /*
    return true if passing nextProps to render would return
    the same result as passing prevProps to render,
    otherwise return false
    */
    const isPropsEqual = isCustomDefinitionAttributePropsEqual({
        prevPropsAttributeIndex, prevPropsCustomAttributes, nextPropsAttributeIndex, nextPropsCustomAttributes
    })
    return isPropsEqual
}
const memorized = memo(CustomAttributesMultipleSelectWithImageSupport, areEqual)
export default injectIntl(memorized)


// WEBPACK FOOTER //
// ./src/product/form/components/CustomAttributesDefinition/components/CustomAttributesMultipleSelectWithImageSupport.tsx